"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import CustomDropdown from "@/app/admin/system-managment/components/CustomDropdown";
type Group = {
  id: string;
  name_en: string;
  slug: string;
  sortOrder?: number | null;
  properties: {
    id: string;
    code: string;
    label_en: string;
    label_fa?: string | null;
    kind: "ENUM" | "COLOR";
    sortOrder: number | null;
    options: Array<{
      id: string;
      value_code: string;
      value_en: string;
      value_fa?: string | null;
      hex?: string | null;
      sortOrder: number;
    }>;
  }[];
};
import {
  ProductClientState,
  ProductPropertyAssignment,
  ProductPropertyWithMeta,
} from "@/zod/product.schema";
import { useFormContext, useWatch } from "react-hook-form";

type Property = {
  id: string;
  code: string;
  label_en: string;
  label_fa?: string | null;
  kind: "ENUM" | "COLOR";
  sortOrder?: number | null;
  options: Array<{
    id: string;
    value_code: string;
    value_en: string;
    value_fa?: string | null;
    hex?: string | null;
    sortOrder: number;
  }>;
};

export default function EditProductPropertiesSection({
  productId,
  currentLang = "en",
}: {
  productId?: string;
  currentLang?: "en" | "fa";
}) {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string | null>>({});

  const formContext = useFormContext<ProductClientState>();
  const { control, setValue, getValues } = formContext;
  const watchedAssignments = useWatch({
    control,
    name: "productProperty",
  }) as (ProductPropertyAssignment | ProductPropertyWithMeta)[] | undefined;

  const assignments = useMemo(() => {
    const list = watchedAssignments ?? [];
    return list
      .map<ProductPropertyAssignment | null>((entry) => {
        if (!entry?.propertyId || !entry?.optionId) return null;
        return {
          propertyId: entry.propertyId,
          optionId: entry.optionId,
          groupId: entry.groupId ?? null,
        };
      })
      .filter((entry): entry is ProductPropertyAssignment => entry !== null);
  }, [watchedAssignments]);
  const assignmentsByProperty = useMemo(() => {
    const map = new Map<string, ProductPropertyAssignment>();
    assignments.forEach((assignment) => map.set(assignment.propertyId, assignment));
    return map;
  }, [assignments]);

  const initialAssignmentsAppliedRef = useRef(false);

  const { data: groups } = useQuery<Group[]>({
    queryKey: ["propertyGroups"],
    queryFn: async () => {
      const res = await fetch("/api/property-groups");
      if (!res.ok) throw new Error("Failed to load groups");
      const data = await res.json();
      return (data || []).map((g: Group) => ({
        ...g,
        properties: (g.properties || []).sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        ),
      }));
    },
    staleTime: 0,
  });

  const { data: current } = useQuery<
    ProductClientState["productProperty"]
  >({
    queryKey: ["product-properties", productId],
    queryFn: async () => {
      const res = await fetch(`/api/products/${productId}/properties`);
      if (!res.ok) throw new Error("Failed to load product properties");
      return res.json();
    },
    enabled: Boolean(productId),
    staleTime: 0,
  });

  useEffect(() => {
    initialAssignmentsAppliedRef.current = false;
  }, [productId]);

  useEffect(() => {
    if (!productId || !current) return;
    if (initialAssignmentsAppliedRef.current) return;

    const existing = getValues("productProperty") || [];
    if (existing.length === 0) {
      const sanitized = current
        .map<ProductPropertyAssignment | null>((entry: any) => {
          if (!entry?.propertyId || !entry?.optionId) return null;
          return {
            propertyId: entry.propertyId,
            optionId: entry.optionId,
            groupId: entry.groupId ?? null,
          };
        })
        .filter((entry): entry is ProductPropertyAssignment => entry !== null);

      setValue("productProperty", sanitized, {
        shouldDirty: false,
        shouldValidate: false,
        shouldTouch: false,
      });
    }
    initialAssignmentsAppliedRef.current = true;
  }, [current, productId, getValues, setValue]);

  useEffect(() => {
    if (!groups || groups.length === 0) return;
    if (!selectedGroupId) setSelectedGroupId(groups[0].id);
  }, [groups, selectedGroupId]);

  const propertiesInGroup = useMemo(() => {
    if (!groups || !selectedGroupId) return [] as Property[];
    return (
      groups.find((g) => g.id === selectedGroupId)?.properties || []
    ).map((p) => ({
      id: p.id,
      code: p.code,
      label_en: p.label_en,
      label_fa: p.label_fa ?? "",
      kind: p.kind,
      options: (p.options || []).map((o) => ({
        id: o.id,
        value_code: o.value_code,
        value_en: o.value_en,
        value_fa: o.value_fa ?? "",
        hex: o.hex ?? undefined,
        sortOrder: o.sortOrder,
      })),
      sortOrder: p.sortOrder ?? 0,
    }));
  }, [selectedGroupId, groups]);

  if (!productId) {
    return (
      <div
        className="bg-grayCustom lg:bg-white lg:px-0 px-4 py-1"
        dir={currentLang === "fa" ? "rtl" : "ltr"}
      >
        <div className="border border-gray-200 px-4 rounded-md bg-white overflow-hidden">
          <div className="flex items-center justify-between">
            <h3 className="py-2 text-xl font-bold">
              {currentLang === "fa" ? "ویژگی‌ها" : "Properties"}
            </h3>
          </div>
          <div className="pb-4 text-sm text-gray-600">
            {currentLang === "fa"
              ? "ابتدا محصول را ذخیره کنید تا بتوانید ویژگی‌ها را تعیین کنید."
              : "Save the product first to assign properties."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-grayCustom lg:bg-white lg:px-0 px-4 py-1"
      dir={currentLang === "fa" ? "rtl" : "ltr"}
    >
      <div className="border border-gray-200 px-4 rounded-md bg-white overflow-hidden">
        <div className="flex items-center justify-between">
          <h3 className="py-2 text-xl font-bold">
            {currentLang === "fa" ? "ویژگی‌ها" : "Properties"}
          </h3>
        </div>
        <div className="pb-3 space-y-3">
          <div className="w-full max-w-xs">
            <CustomDropdown
              options={(groups || []).map((g) => ({
                label: g.name_en,
                value: g.id,
              }))}
              value={selectedGroupId || ""}
              onChange={(v) => setSelectedGroupId(v)}
              placeholder={
                currentLang === "fa"
                  ? "انتخاب گروه ویژگی"
                  : "Choose a property group"
              }
              className="w-full"
            />
          </div>

          {selectedGroupId && propertiesInGroup.length === 0 && (
            <p className="text-sm text-gray-500">
              {currentLang === "fa"
                ? "هیچ ویژگی‌ای در این گروه وجود ندارد."
                : "No properties in this group."}
            </p>
          )}

          {selectedGroupId && propertiesInGroup.length > 0 && (
            <div className="space-y-2">
              {propertiesInGroup.map((p) => {
                const assignedEntry = assignmentsByProperty.get(p.id);
                const assignedOptionId = assignedEntry?.optionId || "";
                const error = rowError[p.id] || null;
                const options = [...p.options].sort(
                  (a, b) => a.sortOrder - b.sortOrder
                );

                const updateAssignments = (
                  nextAssignments: ProductPropertyAssignment[]
                ) => {
                  setValue("productProperty", nextAssignments, {
                    shouldDirty: true,
                    shouldValidate: true,
                    shouldTouch: true,
                  });
                };

                const attach = (checked: boolean) => {
                  setRowError((prev) => ({ ...prev, [p.id]: null }));

                  if (checked) {
                    if (!selectedGroupId) {
                      setRowError((prev) => ({
                        ...prev,
                        [p.id]:
                          currentLang === "fa"
                            ? "ابتدا یک گروه انتخاب کنید."
                            : "Select a property group first.",
                      }));
                      return;
                    }

                    const defaultOpt = options[0];
                    if (!defaultOpt) {
                      setRowError((prev) => ({
                        ...prev,
                        [p.id]:
                          currentLang === "fa"
                            ? "هیچ مقداری در دسترس نیست."
                            : "No options available.",
                      }));
                      return;
                    }

                    const withoutCurrent = assignments.filter(
                      (assignment) => assignment.propertyId !== p.id
                    );
                    updateAssignments([
                      ...withoutCurrent,
                      {
                        propertyId: p.id,
                        optionId: defaultOpt.id,
                        groupId: selectedGroupId,
                      },
                    ]);
                  } else {
                    const withoutCurrent = assignments.filter(
                      (assignment) => assignment.propertyId !== p.id
                    );
                    updateAssignments(withoutCurrent);
                  }
                };

                const changeOption = (optId: string) => {
                  setRowError((prev) => ({ ...prev, [p.id]: null }));

                  if (!optId) return;

                  const currentAssignment = assignmentsByProperty.get(p.id);

                  if (currentAssignment) {
                    const updated = assignments.map((assignment) =>
                      assignment.propertyId === p.id
                        ? { ...assignment, optionId: optId }
                        : assignment
                    );
                    updateAssignments(updated);
                    return;
                  }

                  if (!selectedGroupId) {
                    setRowError((prev) => ({
                      ...prev,
                      [p.id]:
                        currentLang === "fa"
                          ? "ابتدا یک گروه انتخاب کنید."
                          : "Select a property group first.",
                    }));
                    return;
                  }

                  updateAssignments([
                    ...assignments,
                    {
                      propertyId: p.id,
                      optionId: optId,
                      groupId: selectedGroupId,
                    },
                  ]);
                };

                return (
                  <div
                    key={p.id}
                    className="border border-gray-200 rounded p-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!assignedOptionId}
                          onChange={(e) => attach(e.target.checked)}
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {currentLang === "fa" && p.label_fa
                              ? p.label_fa
                              : p.label_en}
                          </span>
                          <span className="text-xs text-gray-500">
                            {p.code}
                          </span>
                        </div>
                      </div>
                      <div className="w-64">
                        <CustomDropdown
                          options={options.map((o) => ({
                            label: (
                              <div className="flex items-center gap-2">
                                {p.kind === "COLOR" && o.hex ? (
                                  <span
                                    className="inline-block w-3 h-3 rounded-full border border-gray-300"
                                    style={{
                                      backgroundColor: o.hex || "#000000",
                                    }}
                                  />
                                ) : null}
                                <span>
                                  {currentLang === "fa" && o.value_fa
                                    ? o.value_fa
                                    : o.value_en}
                                </span>
                                {p.kind === "COLOR" && o.hex ? (
                                  <span className="text-[10px] text-gray-400">
                                    {o.hex}
                                  </span>
                                ) : null}
                              </div>
                            ),
                            value: o.id,
                          }))}
                          value={assignedOptionId}
                          onChange={(v) => changeOption(v)}
                          placeholder={
                            assignedOptionId
                              ? currentLang === "fa"
                                ? "تغییر مقدار"
                                : "Change value"
                              : currentLang === "fa"
                              ? "انتخاب مقدار"
                              : "Select value"
                          }
                          className="w-full"
                        />
                      </div>
                    </div>
                    {error && (
                      <p className="text-xs text-red-600 mt-1" role="alert">
                        {error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
